<template>
  <div class="payout">
    <Card>
      <div class="payout__filters-container">
        <div class="payout__filters">
          <DateRangePicker
            :label="$lng.__('lk_webmaster_main', 'Period')"
            ref="picker"
            opens="right"
            :locale-data="{
              firstDay: 1,
              format: 'dd.mm.yyyy',
              daysOfWeek: $lng
                .__('lk_webmaster_main', 'Sun,Mon,Tue,Wed,Thu,Fri,Sat')
                .split(','),
              monthNames: $lng
                .__(
                  'lk_webmaster_main',
                  'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec'
                )
                .split(','),
            }"
            :singleDatePicker="false"
            :timePicker="false"
            :showDropdowns="true"
            :autoApply="true"
            :dateRange="filters.byDate"
            v-model="filters.byDate"
            @update="setDateRange"
            :linkedCalendars="false"
          >
          </DateRangePicker>

          <FormSelect
            :label="$lng.__('lk_webmaster_main', 'Status')"
            :placeholder="$lng.__('lk_webmaster_main', 'Select status')"
            :tooltip="$lng.__('lk_webmaster_main', 'Filter payouts by status')"
            :options="optionsStatus"
            v-model:selected="filters.byStatus"
          ></FormSelect>

          <FormSelect
            :label="$lng.__('lk_webmaster_main', 'Payment system')"
            :placeholder="$lng.__('lk_webmaster_main', 'Select payment system')"
            :tooltip="
              $lng.__('lk_webmaster_main', 'Filter payouts by payment system')
            "
            :options="optionsPaymentSystem"
            v-model:selected="filters.byPaymentSystem"
          ></FormSelect>
        </div>

        <div class="payout__filters__reset">
          <button
            @click="createAutoPayout()"
            class="wv-btn--green wv-btn--text"
          >
            {{ $lng.__('lk_webmaster_main', 'Create Payout') }}
          </button>
          <button
            v-if="isResetFiltersVisible"
            @click="resetFilters()"
            class="wv-btn--green wv-btn--text"
          >
            {{ $lng.__('lk_webmaster_main', 'Reset') }}
          </button>
        </div>
      </div>

      <div class="card__separator"></div>

      <div v-if="!is_loading && !payments.length" class="payout__empty">
        {{ $lng.__('lk_webmaster_main', 'No results found.') }}
      </div>

      <div v-if="is_loading || payments.length" class="payout__table">
        <Loading v-if="is_loading"></Loading>

        <div class="table__wrapper">
          <table v-if="!is_loading" class="table">
            <thead>
              <tr>
                <th class="text-left">
                  <SortingIcons
                    :label="
                      $lng.__('lk_webmaster_payouts', 'Date of formation')
                    "
                    :isActive="sortBy === 'date'"
                    @sorted="setSortingByDate"
                  ></SortingIcons>
                </th>
                <th class="text-right">
                  <SortingIcons
                    :label="$lng.__('lk_webmaster_payouts', 'Money')"
                    :isActive="sortBy === 'money'"
                    @sorted="setSortingByMoney"
                  ></SortingIcons>
                </th>
                <th>
                  <SortingIcons
                    :label="$lng.__('lk_webmaster_payouts', 'Payment system')"
                    :isActive="sortBy === 'system'"
                    @sorted="setSortingByPaymentSystem"
                  ></SortingIcons>
                </th>
                <th class="text-center">
                  <SortingIcons
                    :label="$lng.__('lk_webmaster_payouts', 'Status')"
                    :isActive="sortBy === 'status'"
                    @sorted="setSortingByStatus"
                  ></SortingIcons>
                </th>
              </tr>
            </thead>
            <tbody v-if="payments">
              <tr v-for="(payment, index) in paymentsSorted" :key="index">
                <td>
                  {{ payment.timestamp }}
                </td>
                <td class="text-right">
                  {{
                    payment.money.currency ? payment.money.currency : '&euro;'
                  }}
                  {{ payment.money.amount }}
                </td>
                <td class="text-center">
                  {{ payment.payment_system.name }}
                </td>
                <td class="text-center">
                  {{ $lng.__('lk_webmaster_payouts', payment.status) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>

    <Modal
      :title="$lng.__('lk_webmaster_payouts', 'Payout')"
      v-if="showModal"
      @close="closeModal()"
    >
      <div class="pt-20 text-center">
        <p class="pb-30">
          {{ $lng.__('lk_webmaster_payouts', '') }}
        </p>
        <div class="payout__input-container">
          <FormInput
            class="mb-40"
            :label="$lng.__('lk_webmaster_main', 'Amount')"
            :placeholder="$lng.__('lk_webmaster_main', 'Amount')"
            v-model="userPayout.money"
            :validations="[
              {
                rule: $validations.isOnlyNumbers(userPayout.money),
                message: $lng.__(
                  'lk_webmaster_main',
                  `Amount must be a number`
                ),
              },
              {
                rule: $validations.isNumberSmallThan(                  
                  Number(0).toFixed(0),
                  Number(this.userPayout.money).toFixed(0)
                ),
                message: $lng.__(
                  'lk_webmaster_main',
                  `Amount must be more than 0`
                ),
              },
              {
                rule: $validations.isNumberSmallThan(                  
                  Number(this.userPayout.money).toFixed(0),
                  Number(this.getUserBalance.amount + 1).toFixed(0)
                ),
                message: $lng.__(
                  'lk_webmaster_main',
                  `Amount can't be more than user balance`
                ),
              },
              {
                rule: !$validations.isStringLengthBiggerThan(this.userPayout.money, String(this.getUserBalance.amount).length),
                message: $lng.__(
                  'lk_webmaster_main',
                  `Amount can't be more than user balance`
                ),
              },
            ]"
          ></FormInput>
          <p class="payout__input__icon" @click="showAllAmount()"> {{ $lng.__('lk_webmaster_payouts', 'max') }}</p>
        </div>

        <div :class="[this.hasValidationErrorsOfCurrency == false ? 'payout__select-container_error' : '','payout__select-container']">
          <FormSelect
            :label="$lng.__('lk_webmaster_user_settings', 'Currency')"
            class="mb-40"
            :showResetOption="false"
            :withIcons="false"
            v-model:selected="userPayout.currency"
            :options="this.getUserCurrencyOptions"
          ></FormSelect>
          <div v-show="this.hasValidationErrorsOfCurrency == false" class="form-select__notice">
              <span>
                  {{ $lng.__('lk_webmaster_payouts', 'Select currency') }}
              </span>
          </div>
        </div>
        <div :class="[this.hasValidationErrorsOfPaymentSystemId == false ? 'payout__select-container_error' : '','payout__select-container']">
          <FormSelect
            :showResetOption="false"
            :withIcons="false"
            :label="$lng.__('lk_webmaster_main', 'Payment system')"
            :placeholder="$lng.__('lk_webmaster_main', 'Select payment system')"
            :options="this.userOptionsPaymentSystems"
            v-model:selected="userPayout.paymentSystemId"
          ></FormSelect>
          <div v-show="this.hasValidationErrorsOfPaymentSystemId == false" class="form-select__notice">
            <span v-if="this.user.payment_systems.length == 0">
                {{ $lng.__('lk_webmaster_payouts', 'Add payment system in your settings') }}.
            </span>
            <span v-else> 
                {{ $lng.__('lk_webmaster_payouts', 'Select payment system') }}.
            </span>
          </div>
        </div>
        <br />
        <br />
        <div class="payout__filters__reset">
          <button @click="showAllPayout()" class="wv-btn--green wv-btn--text">
            {{ $lng.__('lk_webmaster_main', 'Show all') }}
          </button>
          <button @click="confirmPayment()" :class="[!isFormDataValid ? 'disabled': '', 'wv-btn--green wv-btn--text']" :disabled="!isFormDataValid">
            {{ $lng.__('lk_webmaster_main', 'Confirm payment') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
  export default {
    name: 'Payouts',
    props: [],
    data() {
      return {
        sortBy: 'date',
        sortDirection: 'desc',
        is_loading: true,
        filters: {
          byDate: {
            startDate: new Date().addDays(-365),
            endDate: new Date(),
          },
          byPaymentSystem: false,
          byStatus: false,
        },
        showModal: false,
        userPayout: {
          money: 0,
          currency: false,
          paymentSystemId: false
        },
        hasValidationErrorsOfCurrency: null,
        hasValidationErrorsOfPaymentSystemId: null,
      };
    },
    watch: {
      userPayout: {
          handler(val, oldVal) {
            if(val.currency) {
              this.checkValidationErrors('currency');
            }
            if(val.paymentSystemId) {
              this.checkValidationErrors('paymentSystemId');
            }
          },
          deep: true,
      }
    },
    computed: {
      statMini() {
        return this.$store.state.webmaster.statMini || false;
      },
      user() {
        return this.$store.state.auth.user;
      },
      getUserBalance() {
        return this.statMini.balance.reduce((acc, num) => {return num.amount > acc.amount ? num : acc}, this.statMini.balance[0]);
      },
      getUserCurrencyOptions() {
        return collect(this.statMini.balance)
          .pluck('currency')
          .map((currency) => {
            return {
              id: currency,
              name: currency,
            };
          })
          .all();
      },
      userOptionsPaymentSystems() {
        let userPaymentSystemsArray = [];
        let userPaymentSystemsModify = this.user.payment_systems.map((item) => {
          if(item.paymentSystemData.length) {
            if(item.id == 1 || item.id == 2  || item.id == 4 || item.id == 16 || item.id == 17 || item.id == 18 || item.id == 33) {
              item.name += ` (${item.paymentSystemData[0].current_value})`
            }
            if(item.id == 15 || item.id == 23 || item.id == 25 || item.id == 32 || item.id == 54) {
              item.name += ` (${item.paymentSystemData[1].current_value})`
            }
            if(item.id == 24) {
              item.name += ` (${item.paymentSystemData[7].current_value})`
            }
            item.account_id = item.paymentSystemData[item.paymentSystemData.length-1].current_value
          }          
          return {
            id: item.account_id,
            name: item.name.replace(/^(.{48}).*$/, '$1...)'),
          };
        });
        let userPaymentSystems = collect(userPaymentSystemsModify)
         .pluck('name', 'id')
         .map((name, id) => {
           return {
             id: id,
             name: name,
           };
         })
         .all();
         
        for (const property in userPaymentSystems) {
          userPaymentSystemsArray.push({id:property, name:userPaymentSystems[property].name})
        }
        return userPaymentSystemsArray
      },
      payments() {
        return this.$store.state.webmaster.payments;
      },
      isResetFiltersVisible() {
        return this.filters.byPaymentSystem || this.filters.byStatus;
      },

      // фильтрации
      paymentsFiltered() {
        return this.payments.filter((payment) => {
          let pass = true;

          // фильтр по статусу
          if (this.filters.byStatus) {
            pass = this.filters.byStatus === payment.status;
          }

          // фильтр по платежной системе
          if (pass && this.filters.byPaymentSystem) {
            pass = this.filters.byPaymentSystem === payment.payment_system.name;
          }

          return pass;
        });
      },

      // сортировки
      paymentsSorted() {
        let sorted = this.paymentsFiltered.sort((a, b) => {
          // сортировка по status asc
          if (this.sortBy === 'status' && this.sortDirection === 'asc') {
            return a.status.localeCompare(b.status);
          }

          // сортировка по payment_system asc
          if (this.sortBy === 'system') {
            let first =
              typeof a.payment_system.name === 'object'
                ? ''
                : a.payment_system.name;
            let last =
              typeof b.payment_system.name === 'object'
                ? ''
                : b.payment_system.name;
            return first.localeCompare(last);
          }

          // сортировка по money asc
          if (this.sortBy === 'money') {
            return parseFloat(a.money) - parseFloat(b.money);
          }

          // сортировка по date asc
          if (this.sortBy === 'date') {
            let first = typeof a.timestamp === 'object' ? '' : a.timestamp;
            let last = typeof b.timestamp === 'object' ? '' : b.timestamp;
            return first.localeCompare(last);
          }
        });

        let needReverse = this.sortDirection === 'desc';

        return needReverse ? sorted.reverse() : sorted;
      },
      optionsPaymentSystem() {
        return collect(this.payments)
          .pluck('payment_system')
          .pluck('name')
          .unique()
          .whereNotNull()
          .map((name) => {
            return {
              id: name,
              name: name,
            };
          })
          .all();
      },
      optionsStatus() {
        return collect(this.payments)
          .pluck('status')
          .unique()
          .whereNotNull()
          .map((status) => {
            return {
              id: status,
              name: this.$lng.__('lk_webmaster_payouts', status),
            };
          })
          .all();
      },
      isFormDataValid() {
        if (this.userPayout.currency != false) {
          this.hasValidationErrorsOfCurrency = true;
        } else {
          this.hasValidationErrorsOfCurrency = false;
          return false;
        }
        if (this.userPayout.paymentSystemId != false) {
          this.hasValidationErrorsOfPaymentSystemId = true;
        } else {
          this.hasValidationErrorsOfPaymentSystemId = false;
          return false;
        }
        return this.$validations.isStringNotEmpty(this.userPayout.currency)
          && this.$validations.isStringNotEmpty(this.userPayout.paymentSystemId)
          && this.$validations.isNumberSmallThan(Number(0).toFixed(0), Number(this.userPayout.money).toFixed(0))
          && this.$validations.isNumberSmallThan(Number(this.userPayout.money).toFixed(0), Number(this.getUserBalance.amount + 1).toFixed(0))
          && !this.$validations.isStringLengthBiggerThan(this.userPayout.money, String(this.getUserBalance.amount).length)
      },
    },
    methods: {
      setSorting(by, direction) {
        this.sortBy = by;
        this.sortDirection = direction;
      },
      setSortingByDate(direction) {
        this.setSorting('date', direction);
      },
      setSortingByMoney(direction) {
        this.setSorting('money', direction);
      },
      setSortingByPaymentSystem(direction) {
        this.setSorting('system', direction);
      },
      setSortingByStatus(direction) {
        this.setSorting('status', direction);
      },
      resetFilters() {
        this.filters = {
          ...this.filters,
          byPaymentSystem: false,
          byStatus: false,
        };
      },
      setDateRange(dateRange) {
        this.filters.byDate = dateRange;
        this.is_loading = true;
        this.getPayoutsData();
      },
      getPayoutsData() {
        this.$store
          .dispatch('webmaster/getPayments', {
            from:
              this.filters.byDate.startDate.getFormattedDate() + ' 00:00:00',
            to: this.filters.byDate.endDate.getFormattedDate() + ' 23:59:59',
          })
          .then(() => {
            this.is_loading = false;
          });
      },
      createAutoPayout() {
        this.showModal = true;
      },
      closeModal() {
        this.showModal = false;
      },
      showAllAmount(){
        this.userPayout.money = Number(this.getUserBalance.amount);
      },
      showAllPayout() {
        let defaultPaymentSystemId = this.user.default_payment_system_id.length > 0 ? this.user.default_payment_system_id : 0;
        let currentPaymentSystem = '';

        if(defaultPaymentSystemId === 0) {
          currentPaymentSystem = this.user.payment_systems[0];
        } else {
          currentPaymentSystem = this.user.payment_systems.find(item => item.id === defaultPaymentSystemId);
        }
        
        this.showAllAmount();        
        this.userPayout.currency = this.getUserBalance.currency;
        if(this.userPayout.paymentSystemId === false) {
          this.userPayout.paymentSystemId = `${currentPaymentSystem.account_id}`;
        }             
      },
      checkValidationErrors(value){
        if (this.userPayout[`${value}`] != false && value == 'currency') {
          this.hasValidationErrorsOfCurrency = true;
        }
        if (this.userPayout[`${value}`] != false && value == 'paymentSystemId') {
          this.hasValidationErrorsOfPaymentSystemId = true;
        }
      },
      confirmPayment() {
        WV.Bus.emit('fireValidations');
        if (this.isFormDataValid) {
          this.$store.dispatch('webmaster/createPayout', this.userPayout)
          this.closeModal();
        }
      },
    },
    beforeMount() {
      this.getPayoutsData();
    },
  };
</script>

<style lang="scss">
  @import '../../../scss/utils/mixins.scss';
  @import '../../../scss/utils/variables.scss';

  .payout {
    width: 100%;
    margin-top: 90px;
    @include break(md) {
      margin-top: 0;
    }
    &__filters {
      display: flex;
      flex-direction: column;
      margin-top: -30px;
      padding-bottom: 30px;
      @include break(lg) {
        padding-top: 10px;
        flex-direction: row;
      }
      & > * {
        margin-top: 35px;
        @include break(lg) {
          margin-bottom: 0;
          margin-right: 15px;
          &:last-child {
            margin-right: 0;
          }
        }
      }
      &__reset {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-bottom: 35px;
        @include break(sm) {
          flex-direction: row;
        }
        button {
          margin-right: 0;
          margin-bottom: 20px;
          &:last-child {
            margin-right: 0;
            margin-bottom: 0;
          }
          @include break(sm) {
            margin-right: 15px;
            &:last-child {
              margin-right: 0;
            }
          }
        }
      }
    }
    &__table {
      margin: 0 -30px -30px;
    }
    &__empty {
      padding-top: 30px;
      font-weight: 600;
      color: $color-text-muted;
    }
    &__input-container{
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
    }
    &__input__icon{
      position: absolute;
      right: 1px;
      top: calc(50% - 20px);
      transform: translate(0%, -50%);
      font-size: 14px;
      text-transform: uppercase;
      cursor: pointer;
      background: #e1e6eb;
      border-left: 1px solid #e1e6eb;
      width: fit-content;
      padding: 10px 20px;
      height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 0 3px 3px 0;
      outline: none;
      overflow: hidden;
      &:hover{
        opacity: 0.8;
      }
    }
    &__select-container{
      position: relative;
      &_error{
        .custom-select .selected{
          border-color: $color-day-error-dark;
        }
      }      
      .form-select__notice{
        position: absolute;
        top: 100%;
        min-height: 30px;
        font-size: 12px;
        line-height: 14px;
        color: $color-day-error-dark;
      }
    }
  }
</style>
